/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  AppShell,
  Group,
  Space,
  rem,
  useMantineTheme,
  Portal,
  Burger,
  Text,
  Box,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { clamp } from 'es-toolkit';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { useSignOut } from '@fai/friendli-suite/features/auth/use-sign-out';
import { useProjectsInHeader } from '@fai/friendli-suite/features/products/dedicated-endpoints/use-projects-in-header';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { useTeamRole } from '@fai/friendli-suite/features/settings/team-settings/use-role';
import { useTeams } from '@fai/friendli-suite/features/settings/team-settings/use-team';
import { useOpenCanny } from '@fai/friendli-suite/shared/canny/use-open-feedback';
import DocsWidget from '@fai/friendli-suite/shared/mintlify/docs-widget';
import { DedicatedFreeCreditRemaining } from '@fai/friendli-suite/views/common/inference/common/free-credit-remaining';
import DocumentButton from '@fai/friendli-suite/views/layout/header/basic/product/document-button';
import WebsiteButton from '@fai/friendli-suite/views/layout/header/basic/product/website-button';
import { ProfileComboBox } from '@fai/friendli-suite/views/layout/header/profile-combobox';
import { DedicatedEndpointsHeaderMessage } from '@fai/friendli-suite/views/layout/header-messages';
import { HeaderContentContainer } from '@fai/friendli-suite/views/layout/product/header-contents';
import { NavbarContents } from '@fai/friendli-suite/views/layout/product/navbar-contents';
import { getProductLinksWithLabel } from '@fai/friendli-suite/views/layout/product/product-links-with-label';
import { ProductNavLinks } from '@fai/friendli-suite/views/layout/product/product-navlinks';
import { TeamComboBox } from '@fai/friendli-suite/views/layout/product/team-combobox';
import { NavigationTabs } from '@fai/ui/components/tabs/navigation-tabs';
import { TextVariants } from '@fai/ui/components/typography/variants';
import { useGlobalScrollTop } from '@fai/util/react-hooks';

import { useProductDetailLinksWithLabel } from './data';
import { DetailHeader } from './detail-header';
import { PortalLogo } from './portal-logo';
import { ProjectDetailComboBox } from './project-detail-combobox';
import {
  useProjectDetailComboBoxItems,
  useProjectDetailComboxStore,
} from './use-de-project-detail-combobox';

const portalZIndex = 100 as const;

const CONSTANTS = {
  mainHeaderHeight: 64,
  subHeaderHeight: 52,
  originalLogoSize: 40,
  burgerSize: 34,
  pinnedLogoSize: 28,
} as const;

function DEProjectDetailLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { teamId = '', projectId = '' } = useParams();
  const isMobile = useMediaQuery(`(max-width: calc(${theme.breakpoints.lg} - 1px))`);

  const { scrollTop } = useGlobalScrollTop();

  // STATES
  const [navbarOpened, { toggle: toggleNavbar }] = useDisclosure();

  const { data: projectList } = useProjectsInHeader(teamId);
  const currentProject = projectList?.find((project) => project.id === projectId);

  // ACTIONS
  const handleSignOut = useSignOut();
  const [handleFeedback] = useOpenCanny({
    postFieldCategory: 'feature-requests-feedback',
  });
  const handleOnClickUserSettings = () => navigate(Paths.userSettings.default);

  // TEAM SELECT SWITCH LIST
  const { teams } = useTeams();
  const { isTeamOwner } = useTeamRole(teamId);
  const selfFilteredTeams = teams?.filter((currentTeam) => currentTeam.id !== teamId) ?? [];

  // PROJECT DETAIL COMBOBOX
  const { projectDetailComboBox, handleProjectDetailComboBoxChange } =
    useProjectDetailComboxStore();
  const projectDetailComboBoxItems = useProjectDetailComboBoxItems({
    teamId,
    isTeamOwner,
    projectList,
  });

  return (
    <AppShell
      navbar={{
        width: 0,
        breakpoint: 'lg',
        collapsed: { mobile: !navbarOpened, desktop: true },
      }}
    >
      {/* Need to be in Portal since AppShell has transform attribute(position fixed will be relatively positioned following AppShell.Header  */}
      {!isMobile && (
        <Portal>
          <PortalLogo
            constants={{
              pinnedLogoSize: CONSTANTS.pinnedLogoSize,
              originalLogoSize: CONSTANTS.originalLogoSize,
              subHeaderHeight: CONSTANTS.subHeaderHeight,
              mainHeaderHeight: CONSTANTS.mainHeaderHeight,
            }}
            logoPath={Paths.team(teamId).default}
            navbarOpened={navbarOpened}
            scrollTop={scrollTop}
            zIndex={portalZIndex}
          />
        </Portal>
      )}
      <header
        style={{
          height: CONSTANTS.mainHeaderHeight,
          backgroundColor: theme.pf.color.fg.white,
        }}
      >
        <HeaderContentContainer
          leftItems={
            <>
              <Space w={isMobile ? theme.pf.layout.md : rem(CONSTANTS.originalLogoSize + 36)} />
              {isMobile ? (
                <Burger
                  h={rem(CONSTANTS.burgerSize)}
                  hiddenFrom="lg"
                  opened={navbarOpened}
                  p={0}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  w={rem(CONSTANTS.burgerSize)}
                  onClick={toggleNavbar}
                />
              ) : (
                <TeamComboBox isTeamOwner={isTeamOwner} teamId={teamId} teams={selfFilteredTeams} />
              )}
              <Group
                gap={theme.pf.layout.xl}
                h="100%"
                px={theme.pf.layout.md}
                visibleFrom="lg"
                wrap="nowrap"
              >
                <ProductNavLinks products={getProductLinksWithLabel(teamId)} />
              </Group>
            </>
          }
          rightItems={
            <>
              <Group gap={theme.pf.layout['2xs']} wrap="nowrap">
                <DocsWidget />
                <WebsiteButton />
                <DocumentButton />
              </Group>

              <Space w={theme.pf.layout.sm} />
              <ProfileComboBox
                handleOnClickUserSettings={handleOnClickUserSettings}
                handleSignOut={handleSignOut}
                handleFeedback={handleFeedback}
              />
              <Space w={theme.pf.layout.md} />
            </>
          }
        />
      </header>

      <DetailHeader
        constants={{
          portalZIndex,
          subHeaderHeight: CONSTANTS.subHeaderHeight,
        }}
      >
        <Group gap={0} h="100%">
          {!isMobile && <Space w={clamp(scrollTop, 24, CONSTANTS.originalLogoSize + 24)} />}
          <Group gap={0} h="100%" style={{ overflowX: 'auto' }} wrap="nowrap">
            {!isMobile && (
              <ProjectDetailComboBox
                combobox={projectDetailComboBox}
                handleOptionSubmit={handleProjectDetailComboBoxChange}
                label={currentProject?.name}
              >
                {projectDetailComboBoxItems}
              </ProjectDetailComboBox>
            )}

            <NavigationTabs
              values={useProductDetailLinksWithLabel({ teamId, projectId })}
              renderTab={({ label }) => <Text variant={TextVariants.TitleXs}>{label}</Text>}
              h="100%"
              px={theme.pf.layout.md}
            />
          </Group>
        </Group>

        <Box visibleFrom="sm">
          <DedicatedFreeCreditRemaining />
        </Box>
      </DetailHeader>

      <AppShell.Navbar
        hiddenFrom="lg"
        maw={rem(375)}
        style={{
          boxShadow: navbarOpened ? theme.shadows.md : undefined,
        }}
      >
        <Group justify="flex-end" px={theme.pf.layout.sm} py={theme.pf.layout['2xs']}>
          <Burger
            h={rem(CONSTANTS.burgerSize)}
            opened={navbarOpened}
            w={rem(CONSTANTS.burgerSize)}
            onClick={toggleNavbar}
          />
        </Group>
        <NavbarContents isTeamOwner={isTeamOwner} teamId={teamId} teams={selfFilteredTeams} />
      </AppShell.Navbar>

      {/* overriding mih is required.
      default is calc(100dvh - AppShell.Header's height)
      but we are not using AppShell.Header component in this layout */}
      <AppShell.Main mih="100%">
        <DedicatedEndpointsHeaderMessage />

        <Suspense>
          <Outlet />
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}

export default DEProjectDetailLayout;
